/**
 * @fileoverview Per-source coverage of the latest draft run (MOR-2334):
 * how many activities were found, or incomplete (with the reason),
 * unavailable, off or not connected — so partial data is never mistaken
 * for "nothing happened".
 */

import { Badge } from "@/ui/primitives";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  coverageColor,
  COVERAGE_SOURCE_LABEL,
  COVERAGE_STATUS_LABEL,
  type SourceCoverage,
} from "./draftSuggestions.helpers";

/** Success reads as a count ("Morgan: 14 activities"); other statuses stay words. */
function coverageLabel(coverage: SourceCoverage): string {
  const source = COVERAGE_SOURCE_LABEL[coverage.source];
  if (coverage.status === "complete") {
    const noun = coverage.records === 1 ? "activity" : "activities";
    return `${source}: ${coverage.records} ${noun}`;
  }
  return `${source}: ${COVERAGE_STATUS_LABEL[coverage.status]}`;
}

export function DraftCoverageChips({
  coverage,
}: {
  coverage: SourceCoverage[];
}) {
  if (coverage.length === 0) return null;
  return (
    <TooltipProvider>
      <ul
        className="flex flex-wrap items-center gap-1"
        aria-label="Source coverage"
      >
        {coverage.map((c) => {
          const label = coverageLabel(c);
          const chip = (
            <Badge color={coverageColor(c.status)} variant="soft" size="sm">
              {/* A zero is shown too: "0 activities" says the source was read
                  and had nothing, which is different from not being read. */}
              {label}
            </Badge>
          );
          return (
            <li key={c.source}>
              {c.detail ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} aria-label={`${label}. ${c.detail}`}>
                      {chip}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    {c.detail}
                  </TooltipContent>
                </Tooltip>
              ) : (
                chip
              )}
            </li>
          );
        })}
      </ul>
    </TooltipProvider>
  );
}
